import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User } from '../services/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  const checkAuth = async () => {
    // Prevent duplicate calls
    if (hasCheckedAuth) {
      return;
    }
    
    // First check if we have a stored token and user
    const token = authService.getToken();
    const storedUser = authService.getStoredUser();
    
    if (!token || !storedUser) {
      // No stored credentials, user is not authenticated
      setUser(null);
      setIsLoading(false);
      setHasCheckedAuth(true);
      return;
    }
    
    // We have stored credentials, validate them with the server
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      // Token is invalid, clear stored data
      console.log('🔐 Stored token is invalid, clearing authentication data');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setIsLoading(false);
      setHasCheckedAuth(true);
    }
  };

  const login = async (username: string, password: string) => {
    console.log('🔐 Login attempt for username:', username);
    const response = await authService.login({ username, password });
    console.log('🔐 Login successful, user:', response.user);
    setUser(response.user);
  };

  const logout = async () => {
    console.log('🔐 Logout called');
    await authService.logout();
    console.log('🔐 Logout completed, clearing user');
    setUser(null);
  };

  useEffect(() => {
    console.log('🔐 AuthContext useEffect triggered');
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 